import { getBearerAuthHeaders } from "@/lib/authSession";
import { fetchWithAuthRetry } from "@/lib/authedRequest";
import { fetchWithTimeout } from "@/lib/fetchWithTimeout";
import { getInboxAgentWorkspaceId, inboxWorkspaceHeaders } from "@/lib/inboxLiveConfig";
import { normalizeTicketStatus } from "@/pages/tickets/api/ticketStorage";
import {
  ZENDESK_FROM_LIVE_CHAT_TIMEOUT_MS,
  ZendeskTicketCreateDebug,
} from "@/lib/zendeskTicketCreateDebug";

export { normalizeTicketStatus } from "@/pages/tickets/api/ticketStorage";

export const TICKET_QUEUE_SECTIONS = [
  { id: "open", label: "Open" },
  { id: "pending", label: "Pending" },
  { id: "on_hold", label: "On hold" },
  { id: "resolved", label: "Resolved" },
];

function normalizeTicketGroup(value) {
  const v = String(value ?? "").trim().toLowerCase();
  return v === "refund" ? "refund" : "technical";
}

function _resolveWorkspaceId(workspaceId) {
  if (workspaceId && String(workspaceId).trim()) return String(workspaceId).trim();
  return getInboxAgentWorkspaceId();
}

function _toTicket(row) {
  const createdAt = row?.created_at ?? row?.createdAt ?? new Date().toISOString();
  return {
    id: String(row?.id ?? ""),
    number: row?.ticket_number ?? row?.number ?? null,
    // True only when a real live-chat conversation backs this ticket. Email-origin
    // tickets have none, so we must NOT fetch a conversation for them (would 404).
    hasConversation: Boolean(row?.conversation_id ?? row?.conversationId),
    conversationId: String(row?.conversation_id ?? row?.conversationId ?? row?.id ?? ""),
    subject: String(row?.subject ?? "Support request"),
    requesterEmail: String(row?.customer_email ?? row?.requesterEmail ?? ""),
    openedBy: String(row?.source ?? "agent") === "email" ? "user" : "agent",
    source: String(row?.source ?? "agent"),
    createdAt,
    status: normalizeTicketStatus(row?.status),
    conversationSummary: String(row?.conversation_summary ?? row?.conversationSummary ?? ""),
    firstAgentReplyAt: row?.first_response_at ?? row?.firstAgentReplyAt ?? null,
    lastAgentReplyAt:
      row?.last_agent_reply_at ?? row?.lastAgentReplyAt ?? row?.first_response_at ?? row?.firstAgentReplyAt ?? null,
    lastRequesterActivityAt:
      row?.last_requester_activity_at ?? row?.lastRequesterActivityAt ?? createdAt,
    statusChangedAt: row?.updated_at ?? row?.statusChangedAt ?? createdAt,
    group: normalizeTicketGroup(row?.group ?? row?.group_key),
  };
}

async function _ticketsFetch(path, { method = "GET", body, workspaceId } = {}) {
  const resolvedWorkspaceId = _resolveWorkspaceId(workspaceId);
  const buildRequest = () => {
    const url = new URL(path, (import.meta.env?.VITE_API_BASE_URL || window.location.origin));
    return fetch(url.toString(), {
      method,
      credentials: "include",
      headers: {
        Accept: "application/json",
        ...getBearerAuthHeaders(),
        ...inboxWorkspaceHeaders(resolvedWorkspaceId),
        ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  };
  const res = await fetchWithAuthRetry(buildRequest);
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const j = await res.json();
      if (j?.detail) {
        if (typeof j.detail === "string") {
          detail = j.detail;
        } else if (typeof j.detail === "object" && j.detail !== null) {
          detail = j.detail.message || j.detail.error || JSON.stringify(j.detail);
        } else {
          detail = JSON.stringify(j.detail);
        }
      }
      if (j?.error) detail = j.error;
    } catch {
      /* ignore */
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.status === 204 ? null : res.json();
}

export async function listTickets({ workspaceId, startDate, endDate } = {}) {
  const sp = new URLSearchParams({ limit: "200" });
  if (startDate) sp.set("start_date", String(startDate));
  if (endDate) sp.set("end_date", String(endDate));
  const payload = await _ticketsFetch(`/api/v1/agent/tickets?${sp.toString()}`, { workspaceId });
  const rows = Array.isArray(payload?.data) ? payload.data : [];
  return {
    data: {
      tickets: rows.map(_toTicket),
      sections: TICKET_QUEUE_SECTIONS.map((s) => ({ ...s })),
    },
  };
}

export async function submitSupportTicket(input, { workspaceId } = {}) {
  const payload = await _ticketsFetch("/api/v1/agent/tickets", {
    method: "POST",
    workspaceId,
    body: {
      conversation_id: input.conversationId,
      subject: input.subject?.trim() || "Support request",
      status: "open",
      source: input.openedBy === "user" ? "email" : "chat",
      customer_email: input.requesterEmail?.trim() || null,
      group: "technical",
    },
  });
  return { data: _toTicket(payload) };
}

/** Convert an active live-chat session into a ticket (closes session, emails visitor, imports transcript). */
export async function submitTicketFromLiveChat(input, { workspaceId } = {}) {
  const resolvedWorkspaceId = _resolveWorkspaceId(workspaceId);
  const debug = new ZendeskTicketCreateDebug();
  const zendeskEnabled = await debug.isEnabled(resolvedWorkspaceId);
  const started = Date.now();
  if (zendeskEnabled) {
    debug.logStart({ workspaceId: resolvedWorkspaceId, conversationId: input.conversationId });
  }

  const buildRequest = () => {
    const url = new URL(
      "/api/v1/agent/tickets/from-live-chat",
      import.meta.env?.VITE_API_BASE_URL || window.location.origin,
    );
    return fetchWithTimeout(
      url.toString(),
      {
        method: "POST",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          ...getBearerAuthHeaders(),
          ...inboxWorkspaceHeaders(resolvedWorkspaceId),
        },
        body: JSON.stringify({
          conversation_id: input.conversationId,
          subject: input.subject?.trim() || "Support request",
          customer_email: input.requesterEmail?.trim() || null,
          group: "technical",
        }),
      },
      ZENDESK_FROM_LIVE_CHAT_TIMEOUT_MS,
    );
  };

  const res = await fetchWithAuthRetry(buildRequest);
  const durationMs = Date.now() - started;
  const requestId = res.headers.get("X-Request-ID") || res.headers.get("x-request-id");
  let payload = null;
  try {
    payload = await res.json();
  } catch {
    payload = null;
  }

  if (zendeskEnabled) {
    debug.logResult({
      status: res.status,
      durationMs,
      detail: debug.parseDetail(payload),
      requestId,
    });
  }

  if (!res.ok) {
    let detail = res.statusText;
    const d = payload?.detail;
    if (typeof d === "string") {
      detail = d;
    } else if (d && typeof d === "object") {
      detail = d.message || d.error || JSON.stringify(d);
    } else if (payload?.error) {
      detail = payload.error;
    }
    const err = new Error(detail || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }

  const ticket = _toTicket(payload?.ticket ?? payload);
  if (zendeskEnabled) {
    debug.logOk({ ticketId: ticket.id });
  }
  return {
    data: ticket,
    warnings: Array.isArray(payload?.warnings) ? payload.warnings : [],
  };
}

export async function updateTicketStatus(params, { workspaceId } = {}) {
  const payload = await _ticketsFetch(`/api/v1/agent/tickets/${encodeURIComponent(params.ticketId)}/status`, {
    method: "POST",
    workspaceId,
    body: { status: normalizeTicketStatus(params.status) },
  });
  return { data: _toTicket(payload) };
}

export async function replyTicketEmail(params, { workspaceId } = {}) {
  const body = {
    to_email: params.toEmail,
    subject: params.subject || "",
    body_text: params.messageText || null,
    body_html: null,
    from_email: params.fromEmail || null,
    from_name: params.fromName || null,
    set_status: params.setStatus || null,
  };
  if (Array.isArray(params.attachmentIds) && params.attachmentIds.length) {
    body.attachment_ids = params.attachmentIds;
  }
  return _ticketsFetch(`/api/v1/agent/tickets/${encodeURIComponent(params.ticketId)}/reply-email`, {
    method: "POST",
    workspaceId,
    body,
  });
}

export async function submitTicketAgentResponse(params, { workspaceId } = {}) {
  const status = normalizeTicketStatus(params.newStatus);
  const text = (params.messageText || "").trim();
  const attachmentIds = Array.isArray(params.attachmentIds)
    ? params.attachmentIds.filter(Boolean)
    : [];

  if (params.source === "email" && params.senderTab !== "note" && (text || attachmentIds.length)) {
    await replyTicketEmail(
      {
        ticketId: params.ticketId,
        toEmail: params.requesterEmail,
        messageText: text || (attachmentIds.length ? "[Attachment]" : ""),
        fromEmail: params.fromEmail,
        fromName: params.fromName,
        setStatus: status,
        attachmentIds,
        subject: params.subject,
      },
      { workspaceId },
    );
    return updateTicketStatus({ ticketId: params.ticketId, status }, { workspaceId });
  }

  if (text || attachmentIds.length || params.senderTab === "note") {
    const body = {
      sender_type: "agent",
      sender_email: null,
      message_type: params.senderTab === "note" ? "internal_note" : "agent_reply",
      body_text: text || (attachmentIds.length ? "[Attachment]" : null),
      body_html: null,
      is_internal: params.senderTab === "note",
      set_status: status,
    };
    if (attachmentIds.length) body.attachment_ids = attachmentIds;
    await _ticketsFetch(`/api/v1/agent/tickets/${encodeURIComponent(params.ticketId)}/messages`, {
      method: "POST",
      workspaceId,
      body,
    });
  }
  // Return fresh ticket from the status endpoint (not cached list/get reads).
  return updateTicketStatus({ ticketId: params.ticketId, status }, { workspaceId });
}

export async function updateTicketGroup(params, { workspaceId } = {}) {
  const group = normalizeTicketGroup(params.group);
  const payload = await _ticketsFetch(`/api/v1/agent/tickets/${encodeURIComponent(params.ticketId)}`, {
    method: "PATCH",
    workspaceId,
    body: { group },
  });
  return { data: _toTicket(payload) };
}
